import React from "react";
import { Link } from "gatsby";
import { hasValidProtocol } from "../../../utils/utils";
import {
  ExternalLinkWrapperProps,
  InternalLinkWrapperProps,
  LinkWrapperProps,
} from "../../types";

export const LinkOnlyWrapper = ({
  linkData,
  className,
  children,
}: LinkWrapperProps) => {
  const noLink = <span className={className}>{children}</span>;
  if (!linkData) return noLink;
  if (linkData?.system?.type === "internal_link") {
    return (
      <InternalLink linkData={linkData} className={className}>
        {children}
      </InternalLink>
    );
  } else if (linkData?.system?.type === "external_link") {
    return (
      <ExternalLink linkData={linkData} className={className}>
        {children}
      </ExternalLink>
    );
  }
  return noLink;
};

export const LinkWrapper = ({
  linkData,
  className,
  children,
}: LinkWrapperProps) => {
  if (linkData?.system?.type === "internal_link") {
    return (
      <InternalLink linkData={linkData} className={className}>
        {children}
      </InternalLink>
    );
  } else if (linkData?.system?.type === "external_link") {
    return (
      <ExternalLink linkData={linkData} className={className}>
        {children}
      </ExternalLink>
    );
  }
  return <div className={className}>{children}</div>;
};

export const LinkWithLabelWrapper = ({
  linkData,
  className,
  children,
}: LinkWrapperProps) => {
  if (!linkData) return null;

  if (linkData?.system?.type === "internal_link") {
    return (
      <InternalLink linkData={linkData} className={className}>
        {linkData?.elements?.label?.value}
        {children}
      </InternalLink>
    );
  } else if (linkData?.system?.type === "external_link") {
    return (
      <ExternalLink linkData={linkData} className={className}>
        {linkData?.elements?.label?.value}
        {children}
      </ExternalLink>
    );
  }
  // return children;
};

export const LinkWithLabel = ({
  linkData,
  className,
}: Omit<LinkWrapperProps, "children">) => {
  if (!linkData) return null;
  if (linkData?.system?.type === "internal_link") {
    return (
      <InternalLink linkData={linkData} className={className}>
        {linkData?.elements?.label?.value}
      </InternalLink>
    );
  } else if (linkData?.system?.type === "external_link") {
    return (
      <ExternalLink linkData={linkData} className={className}>
        {linkData?.elements?.label?.value}
      </ExternalLink>
    );
  }
  return null;
};

export const InternalLink = ({
  linkData,
  className,
  children,
}: InternalLinkWrapperProps) => {
  if (linkData?.elements.query_params.value) {
    const linkWithParams =
      linkData?.elements.linked_page?.value?.[0]?.url +
      (linkData?.elements.query_params.value || "");
    return (
      <a
        href={linkWithParams}
        className={className}
        target="_top"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  const linkUrl = linkData?.elements.linked_page?.value?.[0]?.url;
  return linkUrl ? (
    <Link className={className} to={linkUrl}>
      {children}
    </Link>
  ) : null;
};

export const InternalLinkWrapper = ({
  linkData,
  className,
  children,
}: InternalLinkWrapperProps) => {
  if (linkData?.elements?.query_params?.value) {
    const linkUrl =
      linkData.elements?.linked_page?.value?.[0]?.url +
      (linkData.elements?.query_params?.value || "");
    return linkUrl ? (
      <Link className={className} to={linkUrl}>
        {children}
      </Link>
    ) : null;
  }
  return linkData?.elements?.linked_page?.value?.[0]?.url ? (
    <Link
      className={className}
      to={linkData.elements?.linked_page?.value?.[0]?.url}
    >
      {children}
    </Link>
  ) : null;
};

export const ExternalLink = ({
  linkData,
  className,
  children,
}: ExternalLinkWrapperProps) => {
  const linkUrl = linkData.elements?.external_page_url?.value;
  return linkData.elements?.external_page_url?.value ? (
    <a
      href={linkUrl}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : null;
};

/**Strictly check the HTTPS protocol prefix */
export const ValidExternalLink = ({
  linkData,
  className,
  children,
}: ExternalLinkWrapperProps) => {
  const linkUrl = linkData.elements?.external_page_url?.value;

  if (!hasValidProtocol(linkUrl)) {
    return null;
  }
  return (
    <a
      href={linkUrl}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  );
};
